import { Navigate, Outlet, useLocation } from 'react-router';

import { FullPageSpinner } from '@/components/common/LoadingState';
import { useAuth } from '@/features/auth/useAuth';

import { paths } from './paths';

/**
 * Gate for pages that require a session.
 *
 * This is real access control in the sense that it mirrors a real server check
 * (`auth:sanctum`), but it is not itself a security boundary — every protected
 * request is authorised again on the backend.
 */
export function ProtectedRoute() {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return <FullPageSpinner label="Checking your session…" />;
  }

  if (status === 'unauthenticated') {
    // `from` lets the sign-in page send the user back where they were headed.
    return <Navigate to={paths.login} replace state={{ from: location }} />;
  }

  /*
   * An account still on the password that was emailed to it must replace it
   * before going anywhere else. This mirrors a server-side rule rather than
   * inventing one: the API answers 403 `password_change_required` on every
   * /my/* route until it is done, so skipping the page would only produce a
   * portal with no documents in it.
   *
   * `replace` so the browser's back button cannot step around it.
   */
  if (user?.must_change_password && location.pathname !== paths.changePassword) {
    return <Navigate to={paths.changePassword} replace />;
  }

  return <Outlet />;
}
